// ============================================================
//  LOGIN SCREEN
//  Admin: Firebase Email + Password (collapsible)
//  Crew:  Username + Date of Birth (DD/MM/YYYY)
// ============================================================

import React, { useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { useCrewStore } from "../../store/crewStore";
import { useProfileStore } from "../../store/profileStore";

const LoginScreen: React.FC = () => {
  const { loginAdmin, loginCrew, authError, clearError } = useAuthStore();
  const crews = useCrewStore((s) => s.crews);
  const profiles = useProfileStore((s) => s.profiles);

  // Admin fields
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  // Crew fields
  const [username, setUsername] = useState("");
  const [dob, setDob] = useState("");
  const [crewErr, setCrewErr] = useState("");

  // ── Admin submit
  const tryAdmin = async () => {
    if (!adminEmail.trim() || !adminPassword) return;
    clearError();
    setAdminLoading(true);
    await loginAdmin(adminEmail.trim(), adminPassword);
    setAdminLoading(false);
  };

  // ── Crew submit
  const tryCrew = () => {
    const uname = username.trim();
    if (!uname) {
      setCrewErr("Please enter your username.");
      return;
    }
    if (!dob) {
      setCrewErr("Date of birth is required.");
      return;
    }

    // Convert DD/MM/YYYY → YYYY-MM-DD for comparison
    const dobParts = dob.split("/");
    const dobIso =
      dobParts.length === 3
        ? `${dobParts[2]}-${dobParts[1].padStart(2, "0")}-${dobParts[0].padStart(2, "0")}`
        : dob;

    const unameLow = uname.toLowerCase();
    const firstLow = unameLow.split(" ")[0];
    const lastLow = unameLow.split(" ").slice(1).join(" ");

    // Build name list from profiles, then crew records
    const allNames: { name: string; dob: string | null }[] = [];
    profiles.forEach((p) => {
      const full = `${p.firstName}${p.lastName ? " " + p.lastName : ""}`;
      const uname = (p as any).username || "";
      // Match by username first (primary identifier in crew portal)
      if (uname && !allNames.find((x) => x.name === uname))
        allNames.push({ name: uname, dob: p.dob || null });
      // Also match by full name if different
      if (
        full.trim() &&
        full !== uname &&
        !allNames.find((x) => x.name === full)
      )
        allNames.push({ name: full, dob: p.dob || null });
    });
    crews.forEach((c) => {
      [c.driver, c.sideman].forEach((m) => {
        if (m?.name && !allNames.find((x) => x.name === m.name))
          allNames.push({ name: m.name, dob: m.dob || null });
      });
    });

    // Match full username OR first name
    const firstMatches = allNames.filter(
      (x) =>
        x.name.toLowerCase() === unameLow ||
        x.name.toLowerCase().split(" ")[0] === firstLow,
    );

    if (!firstMatches.length) {
      setCrewErr("Username not found — check spelling or contact your admin.");
      return;
    }

    let dobMatches = firstMatches.filter((x) => !x.dob || x.dob === dobIso);

    if (lastLow && dobMatches.length > 1) {
      const withLast = dobMatches.filter(
        (x) => x.name.toLowerCase().split(" ").slice(1).join(" ") === lastLow,
      );
      if (withLast.length) dobMatches = withLast;
    }

    if (!dobMatches.length) {
      setCrewErr("Date of birth does not match — check and try again.");
      return;
    }

    setCrewErr("");
    loginCrew(dobMatches[0].name);
  };

  return (
    <div id="fo-login">
      {/* Logo */}
      <div className="fl-logo-wrap" onClick={() => window.location.reload()}>
        <div className="fl-sgk-logo">
          <img src="/img/SGK_logo.png" alt="SGK Home Delivery" />
        </div>
        <div className="fl-brand">SGK Routes</div>
      </div>

      <div className="fl-card">
        {/* ── ADMIN (collapsible) ── */}
        <div
          className="fl-section-label"
          onClick={() => setAdminOpen((o) => !o)}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            userSelect: "none",
          }}
        >
          Admin Access
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            style={{
              transform: adminOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {adminOpen && (
          <>
            <input
              className="fl-input"
              type="email"
              placeholder="Admin email address"
              value={adminEmail}
              onChange={(e) => {
                setAdminEmail(e.target.value);
                clearError();
              }}
              onKeyDown={(e) =>
                e.key === "Enter" &&
                document.getElementById("admin-pass")?.focus()
              }
              autoComplete="email"
            />
            <input
              className="fl-input"
              id="admin-pass"
              type="password"
              placeholder="Password"
              value={adminPassword}
              onChange={(e) => {
                setAdminPassword(e.target.value);
                clearError();
              }}
              onKeyDown={(e) => e.key === "Enter" && tryAdmin()}
              autoComplete="current-password"
            />
            {authError && <div className="fl-error">{authError}</div>}
            <button
              className="fl-btn fl-btn-admin"
              onClick={tryAdmin}
              disabled={adminLoading}
              style={{ opacity: adminLoading ? 0.7 : 1 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="11"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M7 11V7a5 5 0 0 1 10 0v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              {adminLoading ? "Signing in…" : "Admin Dashboard"}
            </button>
          </>
        )}

        <div className="fl-divider" />

        {/* ── CREW ── */}
        <div className="fl-section-label">Driver / Sideman</div>

        <input
          className="fl-input"
          type="text"
          placeholder="Username (required)"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && tryCrew()}
          autoComplete="off"
        />

        <div className="fl-dob-wrap">
          <div className="fl-dob-lbl">
            Date of Birth <span style={{ color: "#e05252" }}>*</span>
          </div>
          <input
            className="fl-input"
            type="text"
            placeholder="DD/MM/YYYY"
            maxLength={10}
            value={dob}
            style={{ marginBottom: 0 }}
            onChange={(e) => {
              let v = e.target.value.replace(/[^\d]/g, "");
              if (v.length > 2) v = v.slice(0, 2) + "/" + v.slice(2);
              if (v.length > 5) v = v.slice(0, 5) + "/" + v.slice(5);
              setDob(v);
            }}
          />
          <div className="fl-req-note">Required to verify your identity</div>
        </div>

        {crewErr && <div className="fl-error">{crewErr}</div>}

        <button className="fl-btn fl-btn-crew" onClick={tryCrew}>
          View My Route
        </button>

        <div className="fl-hint">
          Enter your username and date of birth to see your assigned route.
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
